import React, { useCallback, useEffect, useState } from 'react';

import { AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { ReactComponent as ArrowLeftIcon } from '@pkg/assets/icons/v2/arrow-left.svg';
import { ReactComponent as CloseIcon } from '@pkg/assets/icons/v2/close.svg';
import {
    ConnectedTwitterCard,
    Portal,
    PortalArea,
    YatModalSideSheet,
    YatModalSideSheetSide,
    YatModalSideSheetVariant,
    YatSelector,
} from '@pkg/components';
import {
    redirectToTwitterAuthorizationPage,
    removeTwitterUserData,
    TwitterConnectPageNames,
} from '@pkg/components/TwitterConnect/api';
import { useAppDispatch, useAppSelector, useMedia } from '@pkg/hooks';

import { useLockBodyScroll } from '@app/helpers';
import { TwitterConnectLocalStorage } from '@app/pages/GetStarted/TwitterConnect/types/enums';
import { TWITTER_CONNECT_CONFIRM } from '@app/pages/GetStarted/routes';
import { setTwitterConnectIsOpen } from '@app/store/actions/dashboard';

import {
    CircleButton,
    CloseButton,
    ConnectHeading,
    ConnectModalWrapper,
    ConnectSubheading,
    CtaButton,
    GoBackText,
    MobileHeader,
    MobileWrapper,
    SideSheetInner,
    StyledCtaButton,
} from './styles';

type TwitterConnectStep = 'connectManage' | 'delete' | 'confirmDelete';

export const TwitterConnectModal = () => {
    const { t } = useTranslation('yat_connect');
    const theme = useTheme();
    const isMobile = useMedia(`(max-width: ${theme.breakpoints.mobile})`);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const { twitterConnectIsOpen } = useAppSelector((state) => state.dashboard);

    const user = useAppSelector((state) => state.user);
    const dispatch = useAppDispatch();

    const [step, setStep] = useState<TwitterConnectStep>('connectManage');
    const emojiID = user?.userAccount?.user.eid;
    const twitterData = user?.userClass?.user?.twitter_data;

    const toggleOpen = useCallback(() => {
        dispatch(setTwitterConnectIsOpen(false));
    }, [dispatch]);

    const handleConnect = () => {
        localStorage.setItem(TwitterConnectLocalStorage.UserChangeTwitter, 'true');
        redirectToTwitterAuthorizationPage(
            { redirect_uri: window.location.origin + `/${TWITTER_CONNECT_CONFIRM}` },
            TwitterConnectPageNames.twitterConnectModal,
        );
    };

    useLockBodyScroll(isMobile && twitterConnectIsOpen);

    const handleGoBack = () => setStep('connectManage');
    const handleStepForwards = () => setStep('delete');
    const handleDeleteTwitter = () => {
        setDeleteLoading(true);
        removeTwitterUserData().then(() => {
            setStep('confirmDelete');
            setDeleteLoading(false);
        });
    };

    const step1heading = twitterData ? t('twitter_in_app_heading_manage') : t('twitter_in_app_heading_connect');

    const twitterHandle = twitterData ? `@${twitterData.value.screen_name}` : t('twitter_connect_default');

    useEffect(() => {
        if (!twitterConnectIsOpen) {
            const timeoutid = setTimeout(() => {
                setStep('connectManage');
            }, 500);
            return () => clearTimeout(timeoutid);
        }
    }, [twitterConnectIsOpen]);

    const animationProps = {
        initial: {
            translateY: 50,
            opacity: 0,
        },
        animate: { translateY: 0, opacity: 1 },
        transition: {
            duration: theme.transitions.speeds.normal,
            ease: theme.transitions.eases.cubic,
        },
    };

    const connectManageUI = (
        <SideSheetInner {...animationProps}>
            {emojiID && (
                <YatSelector
                    isSmall
                    disableShine
                    currentEmoji={emojiID}
                    showTwitterHandle
                    animateTwitterHandle
                    twitterHandle={twitterHandle}
                />
            )}
            <ConnectHeading>{step1heading}</ConnectHeading>
            <ConnectSubheading>{t('twitter_in_app_subheading_connect')}</ConnectSubheading>
            {twitterData ? (
                <ConnectedTwitterCard
                    toggleShowDelete={handleStepForwards}
                    handle={twitterHandle}
                    addedDate={twitterData.created_at}
                />
            ) : (
                <CtaButton size="large" onClick={handleConnect}>
                    {t('twitter_in_app_cta')}
                </CtaButton>
            )}
        </SideSheetInner>
    );

    const deleteUI = (
        <>
            <CircleButton onClick={handleGoBack}>
                <ArrowLeftIcon />
            </CircleButton>
            <SideSheetInner {...animationProps}>
                <ConnectHeading>{t('twitter_in_app_heading_delete')}</ConnectHeading>
                <ConnectSubheading>{t('twitter_in_app_subheading_delete')}</ConnectSubheading>
                <StyledCtaButton loading={deleteLoading} color="error" size="large" onClick={handleDeleteTwitter}>
                    {t('twitter_in_app_cta_delete')}
                </StyledCtaButton>
                <GoBackText onClick={handleGoBack}>{t('twitter_in_app_go_back_text')}</GoBackText>
            </SideSheetInner>
        </>
    );

    const deleteConfirmUI = (
        <SideSheetInner {...animationProps}>
            <ConnectHeading>{t('twitter_in_app_heading_delete_confirm')}</ConnectHeading>
            <ConnectSubheading>{t('twitter_in_app_subheading_delete_confirm')}</ConnectSubheading>
            <StyledCtaButton size="large" onClick={toggleOpen}>
                {t('twitter_in_app_cta_delete_confirm')}
            </StyledCtaButton>
            <GoBackText onClick={handleGoBack}>{t('twitter_in_app_textbutton_delete_confirm')}</GoBackText>
        </SideSheetInner>
    );

    const content = twitterConnectIsOpen && (
        <>
            {step === 'connectManage' && connectManageUI}
            {step === 'delete' && deleteUI}
            {step === 'confirmDelete' && deleteConfirmUI}
        </>
    );

    if (isMobile) {
        return (
            <AnimatePresence mode="wait">
                {twitterConnectIsOpen && (
                    <MobileWrapper
                        id="yat-dashboard-mobile-user-twitter-connect"
                        aria-hidden={twitterConnectIsOpen ? 'false' : 'true'}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{
                            ease: theme.transitions.eases.cubic,
                            duration: theme.transitions.speeds.fast,
                        }}
                    >
                        <MobileHeader>{t('twitter_in_app_mobile_heading')}</MobileHeader>
                        <CloseButton onClick={toggleOpen}>
                            <CloseIcon aria-hidden="true" />
                        </CloseButton>
                        <AnimatePresence mode="wait">{content}</AnimatePresence>
                    </MobileWrapper>
                )}
            </AnimatePresence>
        );
    }

    return (
        <Portal destination={PortalArea.Transition}>
            <ConnectModalWrapper className="v2-global-reset">
                <YatModalSideSheet
                    side={YatModalSideSheetSide.right}
                    variant={YatModalSideSheetVariant.roundedGradientSmall}
                    modalLabelId="twitterConnect"
                    open={twitterConnectIsOpen}
                    closeCallback={toggleOpen}
                >
                    <AnimatePresence mode="wait">{content}</AnimatePresence>
                </YatModalSideSheet>
            </ConnectModalWrapper>
        </Portal>
    );
};
